import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from 'vcode-commons-ui'

export interface Bank {
  bankId: string
  name: string
  code: string | null
  isFeatured: boolean
  status: 'approved' | 'pending'
}

/** Compte bancaire. L'IBAN complet ne quitte jamais le serveur. */
export interface BankAccount {
  accountId: string
  label: string
  ibanLast4: string
  bank: Bank
  createdAt: string
}

export interface CreateBankAccount {
  bankId?: string
  newBankName?: string
  label: string
  iban: string
}

export interface PendingBank {
  bankId: string
  name: string
  proposedBy: string | null
  createdAt: string
  accountCount: number
}

const banksKey = ['banks'] as const
const accountsKey = ['accounts'] as const
const pendingKey = ['admin', 'banks', 'pending'] as const

export function useBanks() {
  return useQuery({ queryKey: banksKey, queryFn: () => apiGet<Bank[]>('/api/banks') })
}

export function useBankAccounts() {
  return useQuery({ queryKey: accountsKey, queryFn: () => apiGet<BankAccount[]>('/api/accounts') })
}

export function useCreateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateBankAccount) => apiSend<BankAccount>('/api/accounts', 'POST', request),
    // Pas les banques : une banque ajoutee n'y entre qu'une fois validee.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountsKey }),
  })
}

export function usePendingBanks() {
  return useQuery({ queryKey: pendingKey, queryFn: () => apiGet<PendingBank[]>('/api/admin/banks/pending') })
}

export function useApproveBank() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bankId, name }: { bankId: string; name: string }) =>
      apiSend<null>(`/api/admin/banks/${bankId}/approve`, 'POST', { name }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'banks'] }),
        queryClient.invalidateQueries({ queryKey: banksKey }),
      ]),
  })
}

export function useReplaceBank() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bankId, replacementBankId }: { bankId: string; replacementBankId: string }) =>
      apiSend<{ movedAccounts: number }>(`/api/admin/banks/${bankId}/replace`, 'POST', { replacementBankId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'banks'] }),
  })
}

/** Messages par champ d'un `ValidationProblem`. */
export function fieldErrors(payload: unknown): Record<string, string> {
  if (typeof payload !== 'object' || payload === null) {
    return {}
  }

  const errors = (payload as { errors?: unknown }).errors
  if (typeof errors !== 'object' || errors === null) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>)
      .map(([field, messages]) => [field, Array.isArray(messages) ? messages.join(' ') : ''] as const)
      .filter(([, message]) => message.length > 0),
  )
}
